/**
 * HTML ユーティリティ関数
 */
export default {
  /**
   * メタタグから情報を取得する
   *
   * @param { String } target
   * @return { String | undefined }
   */
  getMeta: (target: string) => {
    const metaObject = document.querySelector<HTMLElement>(`meta${target}`)
    return metaObject ? (metaObject as HTMLMetaElement).content : undefined
  },

  /**
   * HTML DOM エレメントかどうか
   * @param { * } obj 検索対象の要素
   * @returns { Boolean }
   */
  isElement(obj: any) {
    try {
      //Using W3 DOM2 (works for FF, Opera and Chrom)
      return obj instanceof HTMLElement
    } catch (e) {
      //Browsers not supporting W3 DOM2 don't have HTMLElement and
      //an exception is thrown and we end up here. Testing some
      //properties that all elements have. (works on IE7)
      return (
        typeof obj === 'object' &&
        obj.nodeType === 1 &&
        typeof obj.style === 'object' &&
        typeof obj.ownerDocument === 'object'
      )
    }
  },

  /**
   * 文字列内に含まれる HTML 要素を削除し、プレーンテキストのみを抽出する
   * @param str 対象文字列
   * @returns 抽出したプレーンテキスト
   */
  removeHtmlElement(str: string): string {
    const parser = new DOMParser().parseFromString(str, 'text/html')

    // 文字列内に style 句、もしくは script 句がある場合は除外
    parser.querySelectorAll('style, script').forEach(element => element.remove())

    return parser.body.textContent || ''
  },

  /**
   * 文字列内に含まれる HTML 要素を置換文字に退避する
   * @param str 対象文字列
   * @return replaced HTML 部分を「{{$n}}」というインデックス文字列で置換した文字列
   *         extracted 置換された HTML が退避された string 配列
   */
  replaceHtmlElement(str: string): { replaced: string, extracted: string[] } {
    const htmlElementPattern = /<([a-zA-Z][^\s<>/]*)(?:\s+[^<>]*?)?(\/)?>(.*?)<\/\1\s*>|<([a-zA-Z][^\s<>/]*)(?:\s+[^<>]*?)?(\/)?>|<!--[\s\S]*?-->|<!DOCTYPE[\s\S]*?>/g

    // 抽出された HTML 要素を格納しておく配列
    const extractedHtmlElements: string[] = []

    // HTML 要素部分を退避した上で置換文字列に変換
    const replacedText: string = str.replace(htmlElementPattern, (match) => {
      // タグ全体を抽出
      const element = match.trim()
      extractedHtmlElements.push(element)

      // 一時的な置換文字列を返す -> {{n}}
      return `{{${extractedHtmlElements.length - 1}}}`
    })

    return {
      replaced: replacedText,
      extracted: extractedHtmlElements,
    }
  },

  /**
   * 文字列内の URL に相当する箇所を HTML のアンカーに置換する
   * @param str 対象文字列
   * @param label 置換後のアンカーラベル
   * @param target アンカーの target 指定（default: _blank）
   */
  toAnchorFromUrlText(str: string, label?: string, classes?: string[], target: string = '_blank') {
    // 引数で指定された対象文字列内に含まれる HTML 要素を一旦退避し、置換用文字列に変換
    const _str = this.replaceHtmlElement(str)

    // ']))/;正規表現（/〜/）を解釈してくれないエディタ等で自動整形を崩さないため
    const urlPattern = /((h?)(ttps?:\/\/[a-zA-Z0-9.\-_@:/~?%&;=+#',[\]*!]+))/g

    let textWithAnchor = _str.replaced.replace(urlPattern, (all, url, h, href) => {
      const el = document.createElement('a')
      el.href = `h${href}`
      el.target = target
      el.innerText = label || url
      if (classes) el.classList.add(...classes)

      interface Patterns {
        '&lt;': string,
        '&gt;': string,
      }
      const patterns: Patterns = {
        '&lt;': '<',
        '&gt;': '>',
      }
      return el.outerHTML.replace(/&(lt|gt);/g, (match) => {
        return patterns[match as keyof Patterns]
      })
    })

    // 退避しておいた HTML 要素部分をもとに戻す
    _str.extracted.forEach((el, index) => {
      textWithAnchor = textWithAnchor.replace(`{{${index}}}`, el)
    })

    return textWithAnchor
  },

  addLink: (option: { rel?: string, type?: string, href?: string }) => {
    const link = document.createElement('link')
    if (option.rel) link.rel = option.rel
    if (option.type) link.type = option.type
    if (option.href) link.href = option.href
    document.head.appendChild(link)
  },

  /**
   * 引数に渡された html 内の a 要素に対して、遷移先が別ドメインであれば target 属性を _blank に設定する
   * @param str 対象の html 要素
   */
  toTargetBlankForExternalLink: (str: HTMLElement) => {
    const anchorElements = str.querySelectorAll<HTMLAnchorElement>('a[href]')

    anchorElements.forEach((el) => {
      const url = new URL(el.href, location.href)

      if (url.host !== location.host) {
        el.target = '_blank'
        el.rel = 'noopener noreferrer'
      }
    })
  },

  /**
   * 引数に渡された html 内の img 要素に対して、コピーガードを設定する（コピーガード用 data 属性を付与する）
   * @param str 対象の html 要素
   */
  preventImageCopy: (str: HTMLElement) => {
    const imgElements = Array.from(
      str.querySelectorAll('img')
    ) as HTMLImageElement[]

    imgElements.forEach((el) => {
      el.setAttribute('data-copyblock', 'true')
    })
  },
}
